// src/services/emailService.js - Updated with Custom Email Verification

const FUNCTIONS_BASE_URL = 'https://us-central1-relatiescan.cloudfunctions.net';

/**
 * Send team invitations (original functionality)
 * @param {Array} invites - Array of invitation objects
 * @param {Object} teamInfo - Team information
 * @returns {Promise<Object>} Send results
 */
export const sendInvitationEmails = async (invites, teamInfo) => {
  try {
    console.log('📧 Versturen uitnodigingsmails...', { invites, teamInfo });
    
    // Validate input data
    if (!invites || !Array.isArray(invites) || invites.length === 0) {
      throw new Error('Geen geldige uitnodigingen data');
    }
    
    if (!teamInfo || !teamInfo.teamName) {
      throw new Error('Geen geldige team informatie');
    }

    // Format the data correctly for the Firebase Function
    const requestData = {
      invites: invites.map(invite => ({
        email: invite.email || invite, // Handle both string emails and objects
        code: invite.code || generateInviteCode() // Generate code if missing
      })),
      teamInfo: {
        teamName: teamInfo.teamName,
        createdBy: teamInfo.createdBy || 'Thearapeut',
        teamId: teamInfo.teamId || teamInfo.id
      }
    };

    console.log('📤 Verzenden naar Firebase Function:', requestData.invites.length, 'emails');

    const response = await fetch(`${FUNCTIONS_BASE_URL}/sendBatchEmails`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(requestData)
    });

    const responseText = await response.text();
    console.log('🔥 Raw response:', responseText);

    let result;
    try {
      result = JSON.parse(responseText);
    } catch (parseError) {
      console.error('❌ Failed to parse response as JSON:', responseText);
      throw new Error(`Firebase Function returned invalid JSON: ${responseText}`);
    }

    if (!response.ok) {
      console.error('❌ Firebase Function error:', result);
      throw new Error(`Firebase Function error: ${response.status} - ${result.error || 'Unknown error'}`);
    }

    console.log('✅ Emails verzonden:', result);

    // Return summary
    return {
      success: true,
      totalSent: result.totalSent || 0,
      totalErrors: result.totalErrors || 0,
      results: result.results || [],
      errors: result.errors || []
    };

  } catch (error) {
    console.error('❌ Error sending invitation emails:', error);
    
    // Return detailed error info
    return {
      success: false,
      error: error.message,
      totalSent: 0,
      totalErrors: 1,
      errors: [{ email: 'all', error: error.message }]
    };
  }
};

/**
 * NEW: Send custom email verification via Brevo
 * @param {string} email - User email address
 * @param {string} userName - User's name
 * @param {string} verificationToken - Unique verification token
 * @returns {Promise<Object>} Send result
 */
export const sendCustomVerificationEmail = async (email, userName, verificationToken) => {
  try {
    console.log('📧 Versturen custom verificatie email...', { email, userName });
    
    // Validate input
    if (!email || !validateEmail(email)) {
      throw new Error('Geen geldig email adres');
    }
    
    if (!verificationToken) {
      throw new Error('Geen verificatie token');
    }

    // Generate verification URL
    const verificationUrl = `${window.location.origin}/verify-email?token=${verificationToken}&email=${encodeURIComponent(email)}`;
    
    // Prepare email data for Brevo
   const emailData = {
  invites: [{
    email: email,
    name: userName || email.split('@')[0],
    baseUrl: window.location.origin,  // AUTO-DETECT: localhost:3000 of relatiescan.pabb.nl
    code: verificationToken,
    verificationUrl: verificationUrl
  }],
  teamInfo: {
    teamName: "RelatieScan Account Verificatie",
    createdBy: "RelatieScan System"
  },
  emailType: 'verification'
};

    console.log('📤 Verzenden verificatie email naar:', emailData.invites[0].email);

    const response = await fetch(`${FUNCTIONS_BASE_URL}/sendBatchEmails`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(emailData)
    });

    const responseText = await response.text();
    console.log('📥 Verification email response: success');

    let result;
    try {
      result = JSON.parse(responseText);
    } catch (parseError) {
      console.error('❌ Failed to parse verification response:', responseText);
      throw new Error(`Invalid response from email service: ${responseText}`);
    }

    if (!response.ok) {
      console.error('❌ Verification email error:', result);
      throw new Error(`Email service error: ${response.status} - ${result.error || 'Unknown error'}`);
    }

    console.log('✅ Verificatie email verzonden:', result);

    return {
      success: true,
      messageId: result.messageId,
      recipient: email,
      verificationUrl: verificationUrl
    };

  } catch (error) {
    console.error('❌ Error sending verification email:', error);
    
    return {
      success: false,
      error: error.message,
      recipient: email
    };
  }
};

/**
 * NEW: Verify email token via backend
 * @param {string} token - Verification token
 * @param {string} email - Email address
 * @returns {Promise<Object>} Verification result
 */
export const verifyEmailToken = async (token, email) => {
  try {
    console.log('🔐 Verifying email token...', { token, email });
    
    if (!token || !email) {
      throw new Error('Token en email zijn verplicht');
    }

    const response = await fetch(`${FUNCTIONS_BASE_URL}/verifyEmailToken`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ token, email })
    });

    const result = await response.json();
    
    if (!response.ok) {
      throw new Error(result.error || 'Verificatie mislukt');
    }

    console.log('✅ Email verificatie succesvol:', result);

    return {
      success: true,
      email: email,
      verified: true,
      message: result.message || 'Email succesvol geverifieerd'
    };

  } catch (error) {
    console.error('❌ Email verification error:', error);
    
    return {
      success: false,
      error: error.message,
      email: email,
      verified: false
    };
  }
};

/**
 * Send team invitations with support for new measurement emails
 * @param {Object} emailData - Email data with type flag
 * @returns {Promise<Object>} Send results
 */
export const sendTeamInvites = async (emailData) => {
  try {
    console.log('📧 Versturen team uitnodigingen...', emailData);
    
    // Validate input data
    if (!emailData || !emailData.invitations || !Array.isArray(emailData.invitations)) {
      throw new Error('Geen geldige uitnodigingen data');
    }
    
    if (!emailData.teamName) {
      throw new Error('Geen team naam opgegeven');
    }

    // Determine email type and prepare request data
    const isNewMeasurement = emailData.isNewMeasurement || false;
    
    const requestData = {
      emailType: isNewMeasurement ? 'new_measurement' : 'initial_invitation',
      invites: emailData.invitations.map(invite => ({
        email: invite.email,
        code: invite.inviteCode || invite.code || generateInviteCode(),
        name: invite.name || '',
        isFromPrevious: invite.isFromPrevious || false
      })),
      teamInfo: {
        teamName: emailData.teamName,
        teamId: emailData.teamId,
        createdBy: emailData.createdBy || 'Therapeut',
        measurementPeriod: emailData.measurementPeriod || getCurrentQuarter()
      }
    };

    console.log('📤 Verzenden naar Firebase Function (nieuwe interface):', requestData);

    // Use different endpoint for new measurement emails
    const endpoint = isNewMeasurement ? 'sendNewMeasurementEmails' : 'sendBatchEmails';
    
    const response = await fetch(`${FUNCTIONS_BASE_URL}/${endpoint}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(requestData)
    });

    const responseText = await response.text();
    console.log('🔥 Raw response:', responseText);

    let result;
    try {
      result = JSON.parse(responseText);
    } catch (parseError) {
      console.error('❌ Failed to parse response as JSON:', responseText);
      throw new Error(`Firebase Function returned invalid JSON: ${responseText}`);
    }

    if (!response.ok) {
      console.error('❌ Firebase Function error:', result);
      throw new Error(`Firebase Function error: ${response.status} - ${result.error || 'Unknown error'}`);
    }

    console.log('✅ Emails verzonden:', result);

    // Return standardized results
    return {
      success: true,
      successful: result.results || result.successful || [],
      failed: result.errors || result.failed || [],
      totalSent: result.totalSent || (result.results?.length || 0),
      totalErrors: result.totalErrors || (result.errors?.length || 0),
      emailType: isNewMeasurement ? 'new_measurement' : 'initial_invitation'
    };

  } catch (error) {
    console.error('❌ Error sending team invites:', error);
    
    return {
      success: false,
      error: error.message,
      successful: [],
      failed: emailData.invitations?.map(invite => ({
        email: invite.email,
        error: error.message
      })) || [],
      totalSent: 0,
      totalErrors: emailData.invitations?.length || 1,
      emailType: emailData.isNewMeasurement ? 'new_measurement' : 'initial_invitation'
    };
  }
};

/**
 * Get current quarter for email templates
 * @returns {string} Current quarter (e.g., "Q3 2025")
 */
const getCurrentQuarter = () => {
  const now = new Date();
  const month = now.getMonth() + 1;
  const year = now.getFullYear();
  
  if (month <= 3) return `Q1 ${year}`;
  if (month <= 6) return `Q2 ${year}`;
  if (month <= 9) return `Q3 ${year}`;
  return `Q4 ${year}`;
};

/**
 * Helper function to generate invite codes
 * @returns {string} 8-character alphanumeric code
 */
function generateInviteCode() {
  const characters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
  let result = '';
  for (let i = 0; i < 8; i++) {
    result += characters.charAt(Math.floor(Math.random() * characters.length));
  }
  return result;
}

/**
 * Helper function to generate verification token
 * @returns {string} Secure verification token
 */
export const generateVerificationToken = () => {
  const characters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
  let result = '';
  for (let i = 0; i < 32; i++) {
    result += characters.charAt(Math.floor(Math.random() * characters.length));
  }
  return result;
};

/**
 * Validate email format
 * @param {string} email - Email to validate
 * @returns {boolean} Is valid email
 */
export const validateEmail = (email) => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
};

/**
 * Test function for debugging
 * @returns {Promise<Object>} Test results
 */
export const testEmailFunction = async () => {
  try {
    console.log('🧪 Testing Firebase Function...');
    
    const response = await fetch(`${FUNCTIONS_BASE_URL}/testEmail`, {
      method: 'GET'
    });

    const result = await response.json();
    console.log('🧪 Test result:', result);
    
    return result;
  } catch (error) {
    console.error('❌ Test failed:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Send single test email for debugging
 * @param {string} email - Test email address
 * @param {string} type - Email type ('initial', 'new_measurement', 'verification')
 * @returns {Promise<Object>} Test results
 */
export const sendTestEmail = async (email, type = 'initial') => {
  try {
    console.log('🧪 Sending test email...', { email, type });
    
    // Handle verification email test differently
    if (type === 'verification') {
      return await sendCustomVerificationEmail(email, 'Test User', generateVerificationToken());
    }
    
    const testData = {
      emailType: type === 'new_measurement' ? 'new_measurement' : 'initial_invitation',
      invites: [{
        email: email,
        code: 'TEST1234',
        name: 'Test User'
      }],
      teamInfo: {
        teamName: 'Test Team',
        teamId: 'test-team-id',
        createdBy: 'Test Creator',
        measurementPeriod: getCurrentQuarter()
      }
    };

    const endpoint = type === 'new_measurement' ? 'sendNewMeasurementEmails' : 'sendBatchEmails';
    
    const response = await fetch(`${FUNCTIONS_BASE_URL}/${endpoint}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(testData)
    });

    const result = await response.json();
    console.log('🧪 Test email result:', result);
    
    return {
      success: response.ok,
      result,
      email,
      type
    };
    
  } catch (error) {
    console.error('❌ Test email failed:', error);
    return { 
      success: false, 
      error: error.message,
      email,
      type 
    };
  }
};

/**
 * Get email sending status from Firebase (if implemented)
 * @param {string} jobId - Email job ID
 * @returns {Promise<Object>} Job status
 */
export const getEmailJobStatus = async (jobId) => {
  try {
    const response = await fetch(`${FUNCTIONS_BASE_URL}/getEmailStatus?jobId=${jobId}`, {
      method: 'GET'
    });

    if (!response.ok) {
      throw new Error(`HTTP ${response.status}: ${response.statusText}`);
    }

    const result = await response.json();
    return result;
    
  } catch (error) {
    console.error('❌ Error getting email job status:', error);
    return { 
      success: false, 
      error: error.message 
    };
  }
};

  /**
 * Send custom password reset email via Brevo
 * @param {string} email - User email address
 * @param {string} resetToken - Unique reset token
 * @returns {Promise<Object>} Send result
 */
export const sendCustomPasswordResetEmail = async (email, resetToken) => {
  try {
    console.log('🔐 Versturen custom password reset email...', { email });
    
    // Validate input
    if (!email || !validateEmail(email)) {
      throw new Error('Geen geldig email adres');
    }
    
    if (!resetToken) {
      throw new Error('Geen reset token');
    }

    // Generate reset URL
    const resetUrl = `${window.location.origin}/reset-password?token=${resetToken}&email=${encodeURIComponent(email)}`;
    
    // Prepare email data for Brevo
    const emailData = {
      invites: [{
        email: email,
        name: email.split('@')[0],
        baseUrl: window.location.origin,
        code: resetToken,
        resetUrl: resetUrl
      }],
      teamInfo: {
        teamName: "RelatieScan Wachtwoord Reset",
        createdBy: "RelatieScan System"
      },
      emailType: 'password_reset'
    };

    console.log('📤 Verzenden password reset email naar:', emailData.invites[0].email);

    const response = await fetch(`${FUNCTIONS_BASE_URL}/sendBatchEmails`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(emailData)
    });

    const responseText = await response.text();
    console.log('🔥 Password reset email response: success');

    let result;
    try {
      result = JSON.parse(responseText);
    } catch (parseError) {
      console.error('❌ Failed to parse password reset response:', responseText);
      throw new Error(`Invalid response from email service: ${responseText}`);
    }

    if (!response.ok) {
      console.error('❌ Password reset email error:', result);
      throw new Error(`Email service error: ${response.status} - ${result.error || 'Unknown error'}`);
    }

    console.log('✅ Password reset email verzonden:', result);

    return {
      success: true,
      messageId: result.messageId,
      recipient: email,
      resetUrl: resetUrl
    };

  } catch (error) {
    console.error('❌ Error sending password reset email:', error);
    
    return {
      success: false,
      error: error.message,
      recipient: email
    };
  }
};